import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Download, Eye, Search } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, formatDate, methodLabel } from '../lib/format';
import { HapusCari } from '../components/HapusCari';
import { TotalBar } from '../components/TotalBar';
import { susunTotalBayar } from '../lib/totalBar';

const liso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const periodRange = (p, cs, ce) => {
  const t = new Date();
  if (p === 'today') return [liso(t), liso(t)];
  if (p === 'yesterday') { const d = new Date(t); d.setDate(d.getDate() - 1); return [liso(d), liso(d)]; }
  if (p === 'week') { const d = new Date(t); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return [liso(d), liso(t)]; }
  if (p === 'month') return [liso(new Date(t.getFullYear(), t.getMonth(), 1)), liso(t)];
  if (p === 'last_month') { const f = new Date(t.getFullYear(), t.getMonth() - 1, 1); const l = new Date(t.getFullYear(), t.getMonth(), 0); return [liso(f), liso(l)]; }
  return [cs, ce];
};

const PERIODS = [
  { v: 'today', l: 'Hari Ini' }, { v: 'yesterday', l: 'Kemarin' }, { v: 'week', l: 'Minggu Ini' },
  { v: 'month', l: 'Bulan Ini' }, { v: 'last_month', l: 'Bulan Lalu' }, { v: 'custom', l: 'Custom' },
];
const KINDS = [['', 'Semua'], ['dp', 'DP'], ['pelunasan', 'Pelunasan']];
const METHODS = [['', 'Semua'], ['cash', 'Cash'], ['transfer', 'Transfer'], ['qris', 'QRIS']];
const STATUSES = [['', 'Semua'], ['belum_bayar', 'Belum Bayar'], ['dp', 'DP'], ['dp_bertahap', 'DP Bertahap'], ['lunas', 'Lunas']];
const STATUS_CLS = { lunas: 'bg-success/10 text-success', dp: 'bg-warning/15 text-[#8a5f1e]', dp_bertahap: 'bg-gold/20 text-gold-dark', belum_bayar: 'bg-danger/10 text-danger' };

function FilterPills({ testid, options, value, onChange }) {
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} data-testid={`${testid}-${v || 'semua'}`} onClick={() => onChange(v)}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${value === v ? 'bg-brown text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function PaymentReports() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [cs, setCs] = useState('');
  const [ce, setCe] = useState('');
  const [q, setQ] = useState('');
  const [orderId, setOrderId] = useState('');
  const [kind, setKind] = useState('');
  const [stage, setStage] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    const [s, e] = periodRange(period, cs, ce);
    if (!s || !e) return;
    const params = new URLSearchParams({ start: s, end: e });
    if (q) params.set('q', q);
    if (orderId) params.set('order_id', orderId);
    if (kind) params.set('kind', kind);
    if (stage) params.set('stage', stage);
    if (method) params.set('method', method);
    if (status) params.set('status', status);
    api.get(`/reports/payments?${params}`).then((r) => setData({ ...r.data, rentang: [s, e] })).catch((err) => toast.error(apiError(err)));
  }, [period, cs, ce, q, orderId, kind, stage, method, status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const stages = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    for (const p of data.payments) if (/^DP \d+$/.test(p.stage)) set.add(p.stage);
    return [...set].sort((a, b) => parseInt(a.slice(3)) - parseInt(b.slice(3)));
  }, [data]);

  const doExport = async (allData) => {
    setExporting(true);
    const tid = toast.loading('Sedang membuat laporan pembayaran...');
    try {
      const params = new URLSearchParams();
      if (allData) {
        params.set('all_data', 'true');
      } else {
        const [s, e] = periodRange(period, cs, ce);
        params.set('start', s); params.set('end', e);
        if (q) params.set('q', q);
        if (orderId) params.set('order_id', orderId);
        if (kind) params.set('kind', kind);
        if (stage) params.set('stage', stage);
        if (method) params.set('method', method);
        if (status) params.set('status', status);
      }
      const r = await api.get(`/export/payments?${params}`, { responseType: 'blob' });
      const dispo = r.headers['content-disposition'] || '';
      const fname = dispo.match(/filename="?([^";]+)"?/)?.[1] || 'Laporan_Pembayaran_Deyran_Gallery.xlsx';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success('Laporan pembayaran berhasil dibuat.', { id: tid });
    } catch (err) {
      toast.error(apiError(err, 'Tidak ada pembayaran yang sesuai dengan filter.'), { id: tid });
    } finally {
      setExporting(false);
    }
  };

  const stat = (testid, label, value, tone = 'text-brown-dark') => (
    <div data-testid={testid} className="bg-white rounded-2xl border border-border shadow-soft p-4">
      <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">{label}</p>
      <p className={`font-display text-xl md:text-2xl mt-1.5 tracking-tight ${tone}`}>{value}</p>
    </div>
  );

  const KIND_L = { dp: 'DP', deposit: 'DP', pelunasan: 'Pelunasan', pembayaran: 'Pembayaran' };

  return (
    <div className="space-y-5" data-testid="payment-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Laporan Pembayaran</h1>
          <p className="text-sm text-mocha mt-1">Seluruh aktivitas pembayaran customer — DP, DP bertahap, dan pelunasan.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-filtered-button" disabled={exporting} onClick={() => doExport(false)}
            className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-4 py-2.5 text-sm font-semibold hover:bg-brown-med disabled:opacity-60">
            <Download size={15} /> Export Sesuai Filter
          </button>
          <button data-testid="export-all-button" disabled={exporting} onClick={() => doExport(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-border text-brown px-4 py-2.5 text-sm font-semibold hover:bg-ivory disabled:opacity-60">
            <Download size={15} /> Export Semua
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-4 space-y-3" data-testid="payment-filters">
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => (
            <button key={p.v} data-testid={`payrep-period-${p.v}`} onClick={() => setPeriod(p.v)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${period === p.v ? 'bg-brown text-white' : 'bg-ivory border border-border text-mocha hover:bg-white'}`}>
              {p.l}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input data-testid="payrep-custom-start" type="date" value={cs} onChange={(e) => setCs(e.target.value)} className="rounded-xl border border-input bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30" />
              <span className="text-xs text-mocha">s/d</span>
              <input data-testid="payrep-custom-end" type="date" value={ce} onChange={(e) => setCe(e.target.value)} className="rounded-xl border border-input bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mocha" />
            <input data-testid="payrep-customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama customer / no WA…"
              className="w-full rounded-xl border border-border bg-ivory/50 pl-9 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            <HapusCari show={!!q} onClear={() => setQ('')} testid="payrep-customer-clear" />
          </div>
          <div className="relative">
            <input data-testid="payrep-order-search" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Cari ID Order (mis. 2608-101)…"
              className="w-full rounded-xl border border-border bg-ivory/50 pl-3.5 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            <HapusCari show={!!orderId} onClear={() => setOrderId('')} testid="payrep-order-clear" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[10px] uppercase tracking-wider text-mocha font-bold w-16">Jenis</span>
            <FilterPills testid="payrep-kind" options={KINDS} value={kind} onChange={setKind} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[10px] uppercase tracking-wider text-mocha font-bold w-16">Tahap</span>
            <FilterPills testid="payrep-stage" options={[['', 'Semua'], ...stages.map((s) => [s, s])]} value={stage} onChange={setStage} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[10px] uppercase tracking-wider text-mocha font-bold w-16">Metode</span>
            <FilterPills testid="payrep-method" options={METHODS} value={method} onChange={setMethod} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[10px] uppercase tracking-wider text-mocha font-bold w-16">Status</span>
            <FilterPills testid="payrep-status" options={STATUSES} value={status} onChange={setStatus} />
          </div>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-mocha py-10 text-center">Memuat laporan pembayaran…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="payrep-summary">
            {stat('payrep-total-payments', 'Total Pembayaran', data.summary.total_payments)}
            {stat('payrep-uang-masuk', 'Total Uang Masuk', formatIDR(data.summary.total_uang_masuk))}
            {stat('payrep-total-dp', 'Total DP', formatIDR(data.summary.total_dp))}
            {stat('payrep-total-pelunasan', 'Total Pelunasan', formatIDR(data.summary.total_pelunasan))}
            {stat('payrep-belum-lunas', 'Belum Lunas', `${data.summary.orders_belum_lunas} order`, 'text-danger')}
            {stat('payrep-lunas', 'Lunas', `${data.summary.orders_lunas} order`, 'text-success')}
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="payrep-method-recap">
            <h2 className="text-xs font-bold uppercase tracking-wider text-mocha mb-3">Rekap Metode Pembayaran</h2>
            <div className="flex gap-2 flex-wrap">
              {data.method_recap.length === 0 && <p className="text-xs text-mocha">Tidak ada pembayaran pada filter ini.</p>}
              {data.method_recap.map((m) => (
                <div key={m.method} data-testid={`payrep-recap-${m.method}`} className="rounded-xl bg-ivory border border-border px-4 py-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">{methodLabel(m.method)}</p>
                  <p className="font-display text-lg text-brown-dark">{formatIDR(m.total)}</p>
                  <p className="text-[10px] text-mocha">{m.count} transaksi</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-soft overflow-x-auto" data-testid="payrep-table-card">
            <table className="w-full text-sm min-w-[1080px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                  <th className="px-3 py-3 font-bold">No</th>
                  <th className="px-3 py-3 font-bold">ID Order</th>
                  <th className="px-3 py-3 font-bold">Customer</th>
                  <th className="px-3 py-3 font-bold">Bayar Terakhir</th>
                  <th className="px-3 py-3 font-bold text-right">Total Tagihan</th>
                  <th className="px-3 py-3 font-bold text-right">Total DP</th>
                  <th className="px-3 py-3 font-bold text-right">Pelunasan</th>
                  <th className="px-3 py-3 font-bold text-right">Dibayar</th>
                  <th className="px-3 py-3 font-bold text-right">Sisa</th>
                  <th className="px-3 py-3 font-bold text-center">Jml DP</th>
                  <th className="px-3 py-3 font-bold">Status</th>
                  <th className="px-3 py-3 font-bold text-right">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.length === 0 && (
                  <tr><td colSpan={12} className="px-4 py-8 text-center text-sm text-mocha" data-testid="payrep-empty">Tidak ada pembayaran yang sesuai dengan filter.</td></tr>
                )}
                {data.orders.map((o, i) => (
                  <Fragment key={o.transaction_id}>
                    <tr data-testid={`payrep-row-${o.transaction_number}`}
                      onClick={() => setOpenOrder(openOrder === o.transaction_id ? null : o.transaction_id)}
                      className="border-b border-border/60 hover:bg-ivory/60 cursor-pointer">
                      <td className="px-3 py-2.5 text-mocha">{i + 1}</td>
                      <td className="px-3 py-2.5 font-bold text-brown whitespace-nowrap">{o.transaction_number}</td>
                      <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{o.customer_name}</td>
                      <td className="px-3 py-2.5 text-mocha whitespace-nowrap">{formatDate(o.last_payment_date)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">{formatIDR(o.total)}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{formatIDR(o.total_dp)}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{formatIDR(o.total_pelunasan)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">{formatIDR(o.total_paid)}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <span className={o.remaining > 0 ? 'text-danger font-bold' : 'text-success font-bold'}>{formatIDR(o.remaining)}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">{o.dp_count}</td>
                      <td className="px-3 py-2.5">
                        <span data-testid={`payrep-status-${o.transaction_number}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${STATUS_CLS[o.status]}`}>{o.status_label}</span>
                        {o.overpaid && <p className="text-[10px] text-danger font-bold mt-0.5">⚠ Total pembayaran melebihi total tagihan. Silakan periksa transaksi.</p>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button data-testid={`payrep-detail-${o.transaction_number}`} className="p-1.5 rounded-lg text-brown hover:bg-brown/10">
                          {openOrder === o.transaction_id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                      </td>
                    </tr>
                    {openOrder === o.transaction_id && (
                      <tr data-testid={`payrep-history-${o.transaction_number}`}>
                        <td colSpan={12} className="bg-ivory/70 px-6 py-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold uppercase tracking-wider text-mocha">Riwayat Pembayaran {o.transaction_number}</p>
                            <button data-testid={`payrep-open-order-${o.transaction_number}`} onClick={(e) => { e.stopPropagation(); navigate(`/transaksi/${o.transaction_id}`); }}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brown hover:underline">
                              <Eye size={13} /> Lihat Order
                            </button>
                          </div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                                <th className="py-1.5 pr-3 font-bold">No</th>
                                <th className="py-1.5 pr-3 font-bold">Tanggal</th>
                                <th className="py-1.5 pr-3 font-bold">Jenis</th>
                                <th className="py-1.5 pr-3 font-bold">Tahap</th>
                                <th className="py-1.5 pr-3 font-bold">Metode</th>
                                <th className="py-1.5 pr-3 font-bold text-right">Nominal</th>
                                <th className="py-1.5 font-bold">Keterangan</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.payments.filter((p) => p.transaction_id === o.transaction_id)
                                .sort((a, b) => a.date.localeCompare(b.date) || (a.created_at || '').localeCompare(b.created_at || ''))
                                .map((p, j) => (
                                  <tr key={p.id} className="border-b border-border/40">
                                    <td className="py-1.5 pr-3 text-mocha">{j + 1}</td>
                                    <td className="py-1.5 pr-3 whitespace-nowrap">{formatDate(p.date)}</td>
                                    <td className="py-1.5 pr-3">{KIND_L[p.kind] || 'Pembayaran'}</td>
                                    <td className="py-1.5 pr-3 font-semibold">{p.stage}</td>
                                    <td className="py-1.5 pr-3">{methodLabel(p.method)}</td>
                                    <td className="py-1.5 pr-3 text-right font-semibold">{formatIDR(p.amount)}</td>
                                    <td className="py-1.5 text-mocha text-xs">{p.notes || '-'}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          <p className="text-xs mt-2.5 font-semibold text-brown-dark">
                            Total DP = {formatIDR(o.total_dp)} • Total Pelunasan = {formatIDR(o.total_pelunasan)} • Total Dibayar = {formatIDR(o.total - o.remaining)} • Sisa = {formatIDR(o.remaining)} • Status = {o.status_label}
                          </p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {data.orders.length > 0 && (() => {
            const tb = susunTotalBayar(data);
            return <TotalBar testid="payrep-total" judul={tb.judul} items={tb.items} catatan={tb.catatan} />;
          })()}
        </>
      )}
    </div>
  );
}
